import React, { useRef, useState, useEffect } from "react";
import styled, { keyframes, css } from "styled-components";
import { AdobeIcon } from "../../assets/icons/CompanyIcons";

/* ==================== SCROLL REVEAL HOOK ==================== */
const useReveal = (threshold = 0.15) => {
  const ref = useRef<HTMLDivElement>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, vis };
};

/* ==================== COMPONENT ==================== */

const About: React.FC = () => {
  return (
    <Section id="about">
      <Ambient />

      {/* ══ Floating candles — original spread, middle ones removed ══ */}
      <CandleField>
        {[
          { x: 5, y: 8 },    // far left top
          { x: 12, y: 35 },  // left
          { x: 8, y: 65 },   // left lower
          { x: 72, y: 12 },  // right top
          { x: 80, y: 40 },  // right mid
          { x: 68, y: 70 },  // right lower
          { x: 88, y: 55 },  // far right
          { x: 15, y: 80 },  // left bottom
          { x: 92, y: 20 },  // far right top
          { x: 75, y: 85 },  // right bottom
          { x: 3, y: 50 },   // far left mid
        ].map((c, i) => (
          <FloatingCandle key={i} $i={i} $x={c.x} $y={c.y}>
            <CandleBody $h={18 + (i % 4) * 6} />
            <CandleFlame $i={i} />
            <CandleGlow $i={i} />
          </FloatingCandle>
        ))}
      </CandleField>

      {/* ══ Wandering wand — floats like the snitch ══ */}
      <WanderingWand />

      {/* ══ BLOCK 1: Big intro statement ══ */}
      <IntroBlock />

      {/* ══ BLOCK 2: Stats strip ══ */}
      <StatsStrip />

      {/* ══ BLOCK 3: Competitive Programming — editorial ══ */}
      <CPBlock />

      {/* ══ BLOCK 4: Engineering Impact — staggered metrics ══ */}
      <BroomSection>
        <FlyingBroom $id={1} />
        <ImpactBlock />
      </BroomSection>

      {/* ══ BLOCK 5: Certifications & Recognition ══ */}
      <BroomSection>
        <FlyingBroom $id={2} />
        <CertsBlock />
      </BroomSection>

      {/* ══ BLOCK 6: Current Focus — floating tags ══ */}
      <FocusBlock />

      {/* ══ BLOCK 6: Connect ══ */}
      <ConnectBlock />
    </Section>
  );
};

/* ── INTRO ── */
const IntroBlock: React.FC = () => {
  const { ref, vis } = useReveal(0.2);
  return (
    <IntroWrap ref={ref} $vis={vis}>
      <IntroTag>&#9759; Who Am I?</IntroTag>
      <IntroHeading>
        I'm a Software Engineer<br />
        obsessed with getting better<br />
        <IntroAccent>every single week.</IntroAccent>
      </IntroHeading>
      <IntroSub>
        Most people find that unsettling. I find it... necessary.
      </IntroSub>
    </IntroWrap>
  );
};

/* ── STATS STRIP ── */
const STATS = [
  { value: "2244", label: "LeetCode Guardian", sub: "Max Rating · Top 0.8%" },
  { value: "1700+", label: "Problems Solved", sub: "Multi-platform · Codolio", link: "https://codolio.com/profile/jugshaurya" },
  { value: "AIR 2547", label: "GATE '21", sub: "Top 3%" },
  { value: "15+", label: "Production Systems", sub: "Shipped at scale" },
];

const StatsStrip: React.FC = () => {
  const { ref, vis } = useReveal(0.15);
  return (
    <StatsWrap ref={ref}>
      {STATS.map((s, i) => (
        <StatItem key={s.label} $vis={vis} $i={i}
          {...((s as any).link ? { as: "a" as any, href: (s as any).link, target: "_blank", rel: "noopener noreferrer", style: { textDecoration: "none", cursor: "none" } } : {})}
        >
          <StatValue>{s.value}</StatValue>
          <StatLabel>{s.label}</StatLabel>
          <StatSub>
            {s.sub}
            {(s as any).link && <StatLink> ↗</StatLink>}
          </StatSub>
        </StatItem>
      ))}
    </StatsWrap>
  );
};

/* ── CP BLOCK — Editorial split ── */
const CPBlock: React.FC = () => {
  const { ref, vis } = useReveal(0.15);
  return (
    <SplitSection ref={ref} $vis={vis}>
      <SplitLeft>
        <SplitTag>COMPETITIVE PROGRAMMING</SplitTag>
        <SplitTitle>Where I<br /><SplitAccent>sharpen the blade</SplitAccent></SplitTitle>
      </SplitLeft>
      <SplitRight>
        <SplitLine $vis={vis} $i={0}>
          <LineHighlight>LeetCode Guardian</LineHighlight> — 2244 rating, top 0.8% globally
        </SplitLine>
        <SplitLine $vis={vis} $i={1}>
          Best contest ranks: <LineHighlight>394</LineHighlight> and <LineHighlight>491</LineHighlight> among 45,000+ participants
        </SplitLine>
        <SplitLine $vis={vis} $i={2}>
          <LineHighlight>Google Kick Start</LineHighlight> — Rank 632 in Round H
        </SplitLine>
        <SplitLine $vis={vis} $i={3}>
          Regular competitor — it's not a hobby, it's a compulsion
        </SplitLine>
      </SplitRight>
    </SplitSection>
  );
};

/* ── IMPACT BLOCK — Staggered metric cards ── */
const IMPACT_METRICS = [
  { num: "38%", desc: "INP improvement across 10+ high-traffic pages" },
  { num: "95%", desc: "CLS reduction — near-zero layout shift" },
  { num: "200K+", desc: "Users impacted via contests & assessments UI" },
  { num: "10K+", desc: "Recruiter transactions via subscription APIs" },
  { num: "30%", desc: "Recruiter engagement via Campus Pulse tool" },
  { num: "40%", desc: "Deployment time cut, 20×/week frequency" },
  { num: "6+", desc: "React patterns standardized across repos" },
  { num: "10K+", desc: "Applicant responses exported per campaign" },
];

const IMPACT_TECH = ["JavaScript", "TypeScript", "React", "Java", "SpringBoot", "Kafka", "Sass"];

const ImpactBlock: React.FC = () => {
  const { ref, vis } = useReveal(0.12);
  return (
    <ImpactWrap ref={ref}>
      <ImpactHeader $vis={vis}>
        <SplitTag>ENGINEERING IMPACT</SplitTag>
        <SplitTitle>Where the <SplitAccent>damage was done</SplitAccent></SplitTitle>
      </ImpactHeader>

      {/* Current role — Adobe */}
      <ImpactCardOuter $vis={vis} style={{ marginBottom: "1rem" }}>
        <ImpactRole style={{ marginBottom: 0 }}>
          <ImpactRoleLogoAdobe>
            <AdobeIcon size={30} />
          </ImpactRoleLogoAdobe>
          <ImpactRoleInfo>
            <ImpactRoleTitle>Member of Technical Staff 2</ImpactRoleTitle>
            <ImpactRoleCompanyAdobe>Adobe (Document Cloud)</ImpactRoleCompanyAdobe>
            <ImpactRolePeriod>Aug 2026 — Present · Noida, India</ImpactRolePeriod>
          </ImpactRoleInfo>
          <ImpactLiveBadge><LiveDotBlink /> CURRENT ROLE</ImpactLiveBadge>
        </ImpactRole>
      </ImpactCardOuter>

      <ImpactCardOuter $vis={vis}>
        {/* Role header */}
        <ImpactRole>
          <ImpactRoleLogo>
            <DiamondSpin>
              <svg viewBox="0 0 40 40" width="28" height="28" fill="none">
                <defs>
                  <linearGradient id="diaFace1" x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#4a8aff" />
                    <stop offset="100%" stopColor="#2255cc" />
                  </linearGradient>
                  <linearGradient id="diaFace2" x1="1" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6aa4ff" />
                    <stop offset="100%" stopColor="#3366dd" />
                  </linearGradient>
                  <linearGradient id="diaFace3" x1="0.5" y1="0" x2="0.5" y2="1">
                    <stop offset="0%" stopColor="#88bbff" />
                    <stop offset="100%" stopColor="#4477ee" />
                  </linearGradient>
                </defs>
                {/* Top facets */}
                <polygon points="20,2 8,16 20,14" fill="url(#diaFace1)" />
                <polygon points="20,2 32,16 20,14" fill="url(#diaFace2)" />
                {/* Middle band */}
                <polygon points="8,16 20,14 20,18 4,18" fill="url(#diaFace3)" opacity="0.8" />
                <polygon points="32,16 20,14 20,18 36,18" fill="url(#diaFace1)" opacity="0.7" />
                {/* Bottom facets */}
                <polygon points="4,18 20,38 20,18" fill="url(#diaFace2)" />
                <polygon points="36,18 20,38 20,18" fill="url(#diaFace1)" opacity="0.9" />
                {/* Highlight */}
                <polygon points="20,4 14,14 20,12" fill="white" opacity="0.2" />
              </svg>
            </DiamondSpin>
          </ImpactRoleLogo>
          <ImpactRoleInfo>
            <ImpactRoleTitle>Senior Software Engineer</ImpactRoleTitle>
            <ImpactRoleCompany>Infoedge (Naukri Campus)</ImpactRoleCompany>
            <ImpactRolePeriod>Jul 2023 — Aug 2026 · Noida, India</ImpactRolePeriod>
          </ImpactRoleInfo>
          <ImpactPastBadge>3 YEARS</ImpactPastBadge>
        </ImpactRole>

        {/* Description */}
        <ImpactDesc>
          Led recruiter-side frontend architecture, shipped 10+ production features
          across contests, screening & subscription platforms, drove Core Web Vitals
          optimization across high-traffic pages, and built recruiter engagement tools.
        </ImpactDesc>

        {/* Awards — spherical potion vessels with text on right */}
        <AwardsRow $vis={vis}>
          {[
            // { label: "5-Star Annual Rating", color: "#ffcc00", emoji: "⭐" },
            { label: "Quarterly Individual Excellence Award", color: "#ff3355", emoji: "🏆" },
            { label: "Team Collaboration Award", color: "#55aaff", emoji: "🤝" },
          ].filter(Boolean).map((a, i) => (
            <AwardPotion key={a.label} $delay={i * 0.15}>
              <PotionSphere $color={a.color}>
                <svg viewBox="0 0 50 62" width="38" height="48">
                  {/* Neck */}
                  <rect x="19" y="0" width="12" height="10" rx="3" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
                  {/* Cork */}
                  <rect x="17" y="-1" width="16" height="5" rx="2" fill="#6b4a20" />
                  {/* Sphere body */}
                  <circle cx="25" cy="38" r="22" fill="rgba(255,255,255,0.03)" stroke="rgba(255,255,255,0.1)" strokeWidth="0.8" />
                  {/* Neck-to-sphere connector */}
                  <path d="M19 10 Q17 18 8 28" stroke="rgba(255,255,255,0.08)" strokeWidth="0.8" fill="none" />
                  <path d="M31 10 Q33 18 42 28" stroke="rgba(255,255,255,0.08)" strokeWidth="0.8" fill="none" />
                  {/* Liquid */}
                  <clipPath id={`ac-${i}`}><circle cx="25" cy="38" r="21" /></clipPath>
                  <g clipPath={`url(#ac-${i})`}>
                    <rect className="potion-liquid" x="0" y="30" width="50" height="35" fill={a.color} opacity="0.5" />
                    <path className="potion-wave" d={`M0 ${34 + i} Q12 ${30 + i} 25 ${34 + i} Q38 ${38 + i} 50 ${34 + i} L50 65 L0 65 Z`} fill={a.color} opacity="0.35" />
                    <circle className="potion-bubble-1" cx={18 + i * 5} cy="48" r="1.5" fill={a.color} opacity="0.5" />
                    <circle className="potion-bubble-2" cx={30 - i * 2} cy="44" r="1" fill={a.color} opacity="0.4" />
                  </g>
                  {/* Highlight */}
                  <ellipse cx="18" cy="30" rx="6" ry="4" fill="white" opacity="0.06" transform="rotate(-25 18 30)" />
                  {/* Emoji inside sphere */}
                  <text x="25" y="42" textAnchor="middle" dominantBaseline="central" fontSize="16">{a.emoji}</text>
                </svg>
              </PotionSphere>
              <AwardLabel>{a.label}</AwardLabel>
            </AwardPotion>
          ))}
        </AwardsRow>

        {/* Metrics grid */}
        <MetricGrid>
          {IMPACT_METRICS.map((m, i) => (
            <MetricCard key={m.desc} $vis={vis} $i={i}>
              <MetricNum>{m.num}</MetricNum>
              <MetricDesc>{m.desc}</MetricDesc>
            </MetricCard>
          ))}
        </MetricGrid>

        {/* Tech pills */}
        <ImpactTechRow>
          {IMPACT_TECH.map(t => (
            <ImpactTechPill key={t}>{t}</ImpactTechPill>
          ))}
        </ImpactTechRow>
      </ImpactCardOuter>
    </ImpactWrap>
  );
};

/* ── Certifications & Recognition ── */
const CERTS = [
  { icon: "🛡", title: "LeetCode Guardian", sub: "Max Rating 2244 · Top 0.8%", badge: "2244", badgeColor: "#ffa116", year: "2023" },
  { icon: "⚔", title: "1700+ Problems Solved", sub: "Multi-platform via Codolio", badge: "1700+", badgeColor: "#06b6d4", year: "2024", link: "https://codolio.com/profile/jugshaurya" },
  { icon: "🎓", title: "GATE'21 — AIR 2547", sub: "Top 3% among 200K+ candidates", badge: "Top 3%", badgeColor: "#4488ff", year: "2021" },
  { icon: "📝", title: "DU Entrance — AIR 13", sub: "Among 20K+ candidates", badge: "Top 0.1%", badgeColor: "#00ccaa", year: "2020" },
  { icon: "📜", title: "React Nanodegree", sub: "Udacity Certified", badge: "Certified", badgeColor: "#22c55e", year: "2020", link: "https://confirm.udacity.com/QQN2KHS5" },
  { icon: "🏆", title: "Individual Excellence Award", sub: "Issued by Infoedge", badge: "Award", badgeColor: "#ffd700", year: "2025" },
];

const CertsBlock: React.FC = () => {
  const { ref, vis } = useReveal(0.1);
  return (
    <CertsWrap ref={ref}>
      <CertsHeader $vis={vis}>
        <SplitTag>CERTIFICATIONS & RECOGNITION</SplitTag>
        <SplitTitle>Proof of <SplitAccent>the grind</SplitAccent></SplitTitle>
      </CertsHeader>
      <CertsGrid>
        {CERTS.map((c, i) => (
          <CertCard key={c.title} $vis={vis} $i={i} $color={c.badgeColor}
            {...(c.link ? { as: "a" as any, href: c.link, target: "_blank", rel: "noopener noreferrer" } : {})}
          >
            <CertTop>
              <CertIcon>{c.icon}</CertIcon>
              <CertBadge $color={c.badgeColor}>{c.badge}</CertBadge>
            </CertTop>
            <CertTitle>{c.title}</CertTitle>
            <CertSub>{c.sub}</CertSub>
            <CertBottom>
              <CertYear>{c.year}</CertYear>
              {c.link && <CertVerify>Verify →</CertVerify>}
            </CertBottom>
          </CertCard>
        ))}
      </CertsGrid>
    </CertsWrap>
  );
};

/* ── FOCUS BLOCK ── */
const FOCUSES = [
  { emoji: "🏃", text: "Running marathons" },
  { emoji: "⚙", text: "Low-Level Design" },
  { emoji: "🏗", text: "System Design" },
  { emoji: "🌐", text: "Distributed Systems" },
  { emoji: "⚔", text: "Advanced DSA" },
];

const FocusBlock: React.FC = () => {
  const { ref, vis } = useReveal(0.15);
  return (
    <FocusWrap ref={ref} $vis={vis}>
      <FocusTag>CURRENTLY EXPLORING</FocusTag>
      <FocusTitle>What occupies my mind</FocusTitle>
      <FocusRow>
        {FOCUSES.map((f, i) => (
          <FocusChip key={f.text} $vis={vis} $i={i} data-skill={f.text}>
            <FocusEmoji>{f.emoji}</FocusEmoji>
            <FocusText>{f.text}</FocusText>
          </FocusChip>
        ))}
      </FocusRow>
    </FocusWrap>
  );
};

/* ── CONNECT ── */
const ConnectBlock: React.FC = () => {
  const { ref, vis } = useReveal(0.2);
  return (
    <ConnectWrap ref={ref} $vis={vis}>
      <ConnectLine $vis={vis} />
      <ConnectText>
        Open to connecting with engineers building at scale.{" "}
        <ConnectLink href="https://www.linkedin.com/in/jugshaurya/" target="_blank" rel="noopener noreferrer">
          LinkedIn ↗
        </ConnectLink>{" "}
        or{" "}
        <ConnectLink href="mailto:shauryasinghal84@gmail.com">
          Email ↗
        </ConnectLink>
      </ConnectText>
    </ConnectWrap>
  );
};

/* ── Wandering Wand — erratic flight path like the snitch ── */
const WanderingWand: React.FC = () => {
  const ref = useRef<HTMLDivElement>(null);
  const t = useRef(0);

  useEffect(() => {
    let raf: number;
    const animate = () => {
      t.current += 0.004;
      const el = ref.current;
      if (!el) { raf = requestAnimationFrame(animate); return; }
      const parent = el.parentElement;
      if (!parent) { raf = requestAnimationFrame(animate); return; }

      const w = parent.clientWidth;
      const h = parent.clientHeight;
      const cx = w * 0.5;
      const cy = h * 0.4;
      const rx = Math.min(w * 0.35, 350);
      const ry = Math.min(h * 0.25, 200);

      // Lissajous-like erratic path
      const x = cx + Math.sin(t.current * 0.7) * rx + Math.sin(t.current * 2.3) * 40;
      const y = cy + Math.sin(t.current * 0.5) * ry + Math.cos(t.current * 1.8) * 30;
      const rot = Math.sin(t.current * 1.5) * 25 - 30;

      el.style.transform = `translate(${x}px, ${y}px) rotate(${rot}deg)`;
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, []);

  return (
    <WandFloat ref={ref}>
      <svg width="80" height="16" viewBox="0 0 80 16" fill="none">
        <defs>
          <linearGradient id="aboutWandGrad" x1="0" y1="0" x2="1" y2="0">
            <stop offset="0%" stopColor="#2a1a0a" />
            <stop offset="35%" stopColor="#5a3820" />
            <stop offset="65%" stopColor="#4a2a15" />
            <stop offset="100%" stopColor="#1a0e05" />
          </linearGradient>
          <radialGradient id="aboutTipGlow" cx="50%" cy="50%">
            <stop offset="0%" stopColor="#fff" />
            <stop offset="25%" stopColor="#ffd700" />
            <stop offset="60%" stopColor="rgba(255,165,0,0.4)" />
            <stop offset="100%" stopColor="rgba(255,100,0,0)" />
          </radialGradient>
        </defs>
        {/* Wand body */}
        <rect x="16" y="5.5" width="60" height="5" rx="2.5" fill="url(#aboutWandGrad)" />
        {/* Handle knob */}
        <ellipse cx="76" cy="8" rx="3.5" ry="4" fill="#1a0e05" />
        <ellipse cx="73" cy="8" rx="1.5" ry="3" fill="#2a1a0a" />
        {/* Wood grain */}
        <line x1="22" y1="7" x2="60" y2="7" stroke="rgba(140,90,35,0.12)" strokeWidth="0.4" />
        <line x1="28" y1="9" x2="68" y2="9" stroke="rgba(140,90,35,0.08)" strokeWidth="0.4" />
        {/* Glowing tip */}
        <circle cx="14" cy="8" r="8" fill="url(#aboutTipGlow)" opacity="0.8">
          <animate attributeName="r" values="7;10;7" dur="1.5s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.7;1;0.7" dur="1.5s" repeatCount="indefinite" />
        </circle>
        <circle cx="14" cy="8" r="2.5" fill="rgba(255,255,255,0.9)" />
      </svg>
      {/* Trailing sparkles */}
      <WandSparkle $x={20} $y={-6} $d={0.8} />
      <WandSparkle $x={30} $y={4} $d={1.2} />
      <WandSparkle $x={10} $y={8} $d={1.0} />
      <WandSparkle $x={25} $y={-3} $d={1.5} />
    </WandFloat>
  );
};

export default About;

/* ==================== ANIMATIONS ==================== */
/* animations defined inline below */

/* ==================== STYLED ==================== */
const Section = styled.section`
  position: relative;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(180deg, #0a0a0a 0%, #0d0a14 30%, #0d0a14 70%, #0a0a0a 100%);
  overflow: hidden;
`;

const Ambient = styled.div`
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translateX(-50%);
  width: 600px;
  height: 600px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(139,92,246,0.03) 0%, transparent 70%);
  pointer-events: none;
  filter: blur(60px);
`;

/* ── Intro ── */
const IntroWrap = styled.div<{ $vis: boolean }>`
  max-width: 800px;
  margin: 0 auto 4rem;
  text-align: center;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "40px")});
  transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
`;

const IntroTag = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.6rem;
  color: #8b5cf6;
  letter-spacing: 0.25em;
  margin-bottom: 1.5rem;
`;

const IntroHeading = styled.h2`
  font-family: "Cinzel", serif;
  font-size: clamp(1.8rem, 4.5vw, 3.2rem);
  font-weight: 700;
  color: #f0ece8;
  line-height: 1.25;
  margin-bottom: 1.5rem;
`;

const IntroAccent = styled.span`
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const IntroSub = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 1rem;
  font-style: italic;
  color: #7a6a8a;
  line-height: 1.6;
`;

/* ── Stats Strip ── */
const StatsWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  max-width: 900px;
  margin: 0 auto 5rem;
  gap: 0;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem 0;
  }
`;

const StatItem = styled.div<{ $vis: boolean; $i: number }>`
  text-align: center;
  padding: 1.5rem 1rem;
  position: relative;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "25px")});
  transition: all 0.6s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: ${({ $i }) => $i * 0.12}s;

  /* Vertical dividers */
  &:not(:last-child)::after {
    content: "";
    position: absolute;
    right: 0;
    top: 20%;
    height: 60%;
    width: 1px;
    background: linear-gradient(180deg, transparent, rgba(139,92,246,0.15), transparent);
  }

  @media (max-width: 700px) {
    &:nth-child(2)::after { display: none; }
  }
`;

const StatValue = styled.div`
  font-family: "Cinzel", serif;
  font-size: clamp(1.8rem, 3.5vw, 2.5rem);
  font-weight: 700;
  color: #f0ece8;
  line-height: 1;
  margin-bottom: 0.4rem;
`;

const StatLabel = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #8b5cf6;
  letter-spacing: 0.12em;
  margin-bottom: 0.15rem;
`;

const StatSub = styled.div`
  font-family: "Raleway", sans-serif;
  font-size: 0.55rem;
  color: #5a4a6a;
`;

const StatLink = styled.span`
  color: #8b5cf6;
  font-weight: 600;
`;

/* ── Split Section (CP) ── */
const SplitSection = styled.div<{ $vis: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 3rem;
  max-width: 900px;
  margin: 0 auto 5rem;
  align-items: start;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "30px")});
  transition: all 0.7s cubic-bezier(0.16, 1, 0.3, 1);

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const SplitLeft = styled.div`
  position: sticky;
  top: 120px;

  @media (max-width: 700px) {
    position: static;
  }
`;

const SplitTag = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #6a5a8a;
  letter-spacing: 0.25em;
  margin-bottom: 0.5rem;
`;

const SplitTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: clamp(1.4rem, 3vw, 2rem);
  font-weight: 700;
  color: #e0d8e8;
  line-height: 1.3;
`;

const SplitAccent = styled.span`
  color: #8b5cf6;
`;

const SplitRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SplitLine = styled.p<{ $vis: boolean; $i: number }>`
  font-family: "Raleway", sans-serif;
  font-size: 0.88rem;
  color: #9a8aaa;
  line-height: 1.7;
  padding-left: 1.2rem;
  position: relative;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateX(${({ $vis }) => ($vis ? 0 : "20px")});
  transition: all 0.5s cubic-bezier(0.16, 1, 0.3, 1);
  transition-delay: ${({ $i }) => 0.2 + $i * 0.1}s;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 10px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #8b5cf6;
    box-shadow: 0 0 8px rgba(139,92,246,0.4);
  }
`;

const LineHighlight = styled.span`
  color: #e0d8f0;
  font-weight: 600;
`;

/* ── Impact Block (screenshot-style card) ── */
const ImpactWrap = styled.div`
  max-width: 900px;
  margin: 0 auto 5rem;
`;

const ImpactHeader = styled.div<{ $vis: boolean }>`
  margin-bottom: 1.5rem;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "20px")});
  transition: all 0.6s ease;
`;

const ImpactCardOuter = styled.div<{ $vis: boolean }>`
  padding: 2rem;
  background: rgba(18, 14, 28, 0.7);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 16px;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "20px")});
  transition: all 0.7s ease 0.15s;

  &:hover {
    border-color: rgba(139,92,246,0.12);
  }
`;

const ImpactRole = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const diamondRotate = keyframes`
  from { transform: rotateY(0deg); }
  to { transform: rotateY(360deg); }
`;

const DiamondSpin = styled.div`
  animation: ${diamondRotate} 4s linear infinite;
  transform-style: preserve-3d;
  perspective: 100px;
  filter: drop-shadow(0 0 6px rgba(74, 138, 255, 0.4));
`;

const ImpactRoleLogo = styled.div`
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(74, 138, 255, 0.06);
  border: 1px solid rgba(74, 138, 255, 0.12);
  border-radius: 12px;
`;

const ImpactRoleInfo = styled.div`flex: 1;`;

const ImpactRoleTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: 1.15rem;
  font-weight: 700;
  color: #f0ece8;
  margin-bottom: 0.15rem;
`;

const ImpactRoleCompany = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.65rem;
  color: #cc2244;
  font-weight: 600;
`;

const ImpactRolePeriod = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.5rem;
  color: #5a4a6a;
`;

const liveBlink = keyframes`
  0%, 100% { opacity: 1; box-shadow: 0 0 0 0 rgba(34,197,94,0.4); }
  50% { opacity: 0.4; box-shadow: 0 0 0 4px rgba(34,197,94,0); }
`;

const LiveDotBlink = styled.span`
  display: inline-block;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
  margin-right: 0.4rem;
  animation: ${liveBlink} 1.5s ease-in-out infinite;
`;

const ImpactLiveBadge = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.5rem;
  color: #22c55e;
  letter-spacing: 0.12em;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ImpactRoleLogoAdobe = styled(ImpactRoleLogo)`
  background: rgba(250, 15, 0, 0.06);
  border-color: rgba(250, 15, 0, 0.18);
  color: #fa0f00;
`;

const ImpactRoleCompanyAdobe = styled(ImpactRoleCompany)`
  color: #fa0f00;
`;

const ImpactPastBadge = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.5rem;
  color: #7a6a8a;
  letter-spacing: 0.12em;
  flex-shrink: 0;
  display: flex;
  align-items: center;
`;

const ImpactDesc = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.85rem;
  color: #9a8aaa;
  line-height: 1.7;
  margin-bottom: 1.5rem;
`;

/* ── Award Potion Spheres ── */
const potionFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const bubbleUp = keyframes`
  0% { transform: translateY(0); opacity: 0.5; }
  100% { transform: translateY(-12px); opacity: 0; }
`;

const waveMove = keyframes`
  0% { transform: translateX(0); }
  50% { transform: translateX(-8px); }
  100% { transform: translateX(0); }
`;

const liquidPulse = keyframes`
  0%, 100% { transform: translateY(0); }
  30% { transform: translateY(2px); }
  60% { transform: translateY(-2px); }
`;

const AwardsRow = styled.div<{ $vis: boolean }>`
  display: flex;
  justify-content: flex-start;
  gap: 16px;
  margin-bottom: 22px;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "15px")});
  transition: all 0.6s ease 0.3s;
  flex-wrap: wrap;
`;

const AwardPotion = styled.div<{ $delay: number }>`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: default;
  transition: transform 0.3s;

  &:hover { transform: translateY(-3px) scale(1.05); }
`;

const PotionSphere = styled.div<{ $color: string }>`
  position: relative;
  width: 38px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  .potion-liquid { animation: ${liquidPulse} 2.5s ease-in-out infinite; }
  .potion-wave { animation: ${waveMove} 3s ease-in-out infinite; }
  .potion-bubble-1 { animation: ${bubbleUp} 2s ease-out infinite; }
  .potion-bubble-2 { animation: ${bubbleUp} 2.5s ease-out 0.5s infinite; }

  svg {
    filter: drop-shadow(0 2px 10px ${({ $color }) => $color}50) drop-shadow(0 0 15px ${({ $color }) => $color}20);
  }
`;

const AwardLabel = styled.span`
  font-family: "Cinzel", serif;
  font-size: 0.58rem;
  font-weight: 600;
  color: #b0a0c0;
  letter-spacing: 0.03em;
  line-height: 1.3;
`;

const MetricGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.6rem;
  margin-bottom: 1.5rem;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const MetricCard = styled.div<{ $vis: boolean; $i: number }>`
  padding: 1rem;
  border-radius: 12px;
  background: rgba(255,255,255,0.02);
  border: 1px solid rgba(255,255,255,0.06);
  transition: all 0.4s ease;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "15px")});
  transition-delay: ${({ $i }) => 0.3 + $i * 0.08}s;

  &:hover {
    border-color: rgba(139,92,246,0.15);
    background: rgba(255,255,255,0.04);
  }
`;

const MetricNum = styled.div`
  font-family: "Cinzel", serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #cc2244;
  line-height: 1;
  margin-bottom: 0.3rem;
`;

const MetricDesc = styled.div`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #9a8aaa;
  line-height: 1.5;
`;

const ImpactTechRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const ImpactTechPill = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.52rem;
  padding: 0.3rem 0.7rem;
  border-radius: 100px;
  border: 1px solid rgba(139,92,246,0.15);
  color: #8b5cf6;
  letter-spacing: 0.04em;
  transition: all 0.3s ease;

  &:hover {
    border-color: rgba(139,92,246,0.3);
    background: rgba(139,92,246,0.06);
  }
`;

/* ── Certifications & Recognition ── */
const CertsWrap = styled.div`
  max-width: 900px;
  margin: 0 auto 5rem;
`;

const CertsHeader = styled.div<{ $vis: boolean }>`
  margin-bottom: 1.5rem;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "20px")});
  transition: all 0.6s ease;
`;

const CertsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.8rem;

  @media (max-width: 700px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 450px) {
    grid-template-columns: 1fr;
  }
`;

const CertCard = styled.div<{ $vis: boolean; $i: number; $color: string }>`
  padding: 1.2rem;
  border-radius: 14px;
  background: rgba(15, 12, 25, 0.8);
  border: 1px solid rgba(255,255,255,0.06);
  cursor: none;
  text-decoration: none;
  display: block;
  transition: all 0.4s ease;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "20px")});
  transition-delay: ${({ $i }) => 0.15 + $i * 0.08}s;

  &:hover {
    border-color: ${({ $color }) => $color}30;
    transform: translateY(-3px);
    box-shadow: 0 6px 25px rgba(0,0,0,0.3), 0 0 0 1px ${({ $color }) => $color}10;
  }
`;

const CertTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.8rem;
`;

const CertIcon = styled.div`
  font-size: 1.5rem;
`;

const CertBadge = styled.span<{ $color: string }>`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.48rem;
  font-weight: 600;
  color: ${({ $color }) => $color};
  padding: 0.2rem 0.5rem;
  border: 1px solid ${({ $color }) => $color}30;
  border-radius: 6px;
  background: ${({ $color }) => $color}10;
  letter-spacing: 0.05em;
`;

const CertTitle = styled.h4`
  font-family: "Cinzel", serif;
  font-size: 0.85rem;
  font-weight: 700;
  color: #e0d8e8;
  margin-bottom: 0.3rem;
  line-height: 1.3;
`;

const CertSub = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.72rem;
  color: #8a7a9a;
  line-height: 1.5;
  margin-bottom: 0.6rem;
`;

const CertBottom = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CertYear = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #6a5a7a;
  padding: 0.15rem 0.4rem;
  background: rgba(255,255,255,0.03);
  border-radius: 4px;
`;

const CertVerify = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.48rem;
  color: #cc2244;
  letter-spacing: 0.05em;
`;

/* ── Focus ── */
const FocusWrap = styled.div<{ $vis: boolean }>`
  max-width: 700px;
  margin: 0 auto 4rem;
  text-align: center;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "20px")});
  transition: all 0.6s ease;
`;

const FocusTag = styled.p`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.55rem;
  color: #6a5a8a;
  letter-spacing: 0.25em;
  margin-bottom: 0.4rem;
`;

const FocusTitle = styled.h3`
  font-family: "Cinzel", serif;
  font-size: 1.3rem;
  font-weight: 700;
  color: #e0d8e8;
  margin-bottom: 1.5rem;
`;

const FocusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.7rem;
`;

const floatChip = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-4px); }
`;

const soulLeave = keyframes`
  0% { opacity: 0.8; transform: translateY(0) scale(1); filter: blur(0); }
  40% { opacity: 0.5; transform: translateY(-12px) scale(1.05); filter: blur(1px); }
  70% { opacity: 0.2; transform: translateY(-25px) scale(0.95); filter: blur(3px); }
  100% { opacity: 0; transform: translateY(-40px) scale(0.8); filter: blur(5px); }
`;

const FocusChip = styled.div<{ $vis: boolean; $i: number }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 100px;
  cursor: none;
  position: relative;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "15px")});
  transition: all 0.4s ease;
  transition-delay: ${({ $i }) => 0.2 + $i * 0.08}s;
  animation: ${({ $vis, $i }) => $vis ? css`${floatChip} ${3 + $i * 0.5}s ease-in-out infinite` : "none"};
  animation-delay: ${({ $i }) => $i * 0.4}s;

  /* Soul leaving effect — ghostly copy floats up on initial reveal */
  &::after {
    content: attr(data-skill);
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: "JetBrains Mono", monospace;
    font-size: 0.7rem;
    color: rgba(139, 92, 246, 0.6);
    border-radius: 100px;
    pointer-events: none;
    animation: ${({ $vis }) => $vis ? soulLeave : "none"} 1.8s ease-out forwards;
    animation-delay: ${({ $i }) => 0.3 + $i * 0.12}s;
  }

  &:hover {
    border-color: rgba(139, 92, 246, 0.25);
    background: rgba(139, 92, 246, 0.06);
    box-shadow: 0 4px 15px rgba(139, 92, 246, 0.08);
  }
`;

const FocusEmoji = styled.span`font-size: 1.1rem;`;

const FocusText = styled.span`
  font-family: "JetBrains Mono", monospace;
  font-size: 0.7rem;
  color: #b0a0c0;
  letter-spacing: 0.04em;
`;

/* ── Connect ── */
const ConnectWrap = styled.div<{ $vis: boolean }>`
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  opacity: ${({ $vis }) => ($vis ? 1 : 0)};
  transform: translateY(${({ $vis }) => ($vis ? 0 : "15px")});
  transition: all 0.6s ease 0.1s;
`;

const ConnectLine = styled.div<{ $vis: boolean }>`
  width: 40px;
  height: 1px;
  background: linear-gradient(90deg, #8b5cf6, #ec4899);
  margin: 0 auto 1.5rem;
  transform-origin: center;
  transform: scaleX(${({ $vis }) => ($vis ? 1 : 0)});
  transition: transform 0.8s cubic-bezier(0.16, 1, 0.3, 1);
`;

const ConnectText = styled.p`
  font-family: "Raleway", sans-serif;
  font-size: 0.88rem;
  color: #8a7a9a;
  line-height: 1.7;
`;

const ConnectLink = styled.a`
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 600;
  cursor: none;
  transition: color 0.2s ease;
  &:hover { color: #a78bfa; }
`;

/* ==================== FLYING BROOMSTICK ==================== */
const broomFly1 = keyframes`
  0% { transform: translate(-120px, 0) rotate(-5deg); }
  25% { transform: translate(30vw, -20px) rotate(2deg); }
  50% { transform: translate(60vw, 10px) rotate(-3deg); }
  75% { transform: translate(85vw, -15px) rotate(4deg); }
  100% { transform: translate(110vw, 5px) rotate(-2deg); }
`;

const broomFly2 = keyframes`
  0% { transform: translate(110vw, 0) rotate(3deg) scaleX(-1); }
  25% { transform: translate(70vw, -25px) rotate(-2deg) scaleX(-1); }
  50% { transform: translate(35vw, 8px) rotate(4deg) scaleX(-1); }
  75% { transform: translate(10vw, -12px) rotate(-3deg) scaleX(-1); }
  100% { transform: translate(-120px, 5px) rotate(2deg) scaleX(-1); }
`;

const BroomSection = styled.div`
  position: relative;
  overflow: hidden;
`;

const FlyingBroom = styled.div<{ $id: number }>`
  position: absolute;
  top: ${({ $id }) => $id === 1 ? "12%" : "22%"};
  left: 0;
  z-index: 10;
  pointer-events: none;
  animation: ${({ $id }) => $id === 1 ? broomFly1 : broomFly2} ${({ $id }) => $id === 1 ? "20s" : "25s"} linear infinite;
  opacity: 0.3;
  width: 70px;
  height: 35px;

  /* Witch on broomstick — SVG silhouette */
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 50' fill='none'%3E%3C!-- Broomstick --%3E%3Cline x1='15' y1='28' x2='90' y2='28' stroke='%23614a2a' stroke-width='2.5' stroke-linecap='round'/%3E%3C!-- Broom bristles --%3E%3Cpath d='M85 28 Q90 22 95 20 M85 28 Q92 26 96 24 M85 28 Q92 30 96 32 M85 28 Q90 34 95 36' stroke='%23806030' stroke-width='1.2' stroke-linecap='round'/%3E%3C!-- Body/robe --%3E%3Cpath d='M30 28 Q28 20 32 14 Q36 10 38 14 Q40 18 38 24 Q36 28 34 30 Q32 32 30 28Z' fill='%234a2a6a'/%3E%3C!-- Head --%3E%3Ccircle cx='34' cy='11' r='5' fill='%236a4a8a'/%3E%3C!-- Pointed hat --%3E%3Cpath d='M29 9 L34 -2 L39 9' fill='%233a1a5a'/%3E%3Cpath d='M27 10 Q34 7 41 10' fill='%234a2a6a'/%3E%3C!-- Cape flowing back --%3E%3Cpath d='M34 20 Q40 24 50 22 Q55 20 52 26 Q48 30 40 30 Q36 28 34 24Z' fill='%233a1a5a' opacity='0.7'/%3E%3C!-- Arm forward --%3E%3Cline x1='32' y1='18' x2='25' y2='22' stroke='%235a3a7a' stroke-width='1.5' stroke-linecap='round'/%3E%3C!-- Trailing sparkles --%3E%3Ccircle cx='55' cy='25' r='1' fill='%238b5cf6' opacity='0.5'%3E%3Canimate attributeName='opacity' values='0.5;0;0.5' dur='1s' repeatCount='indefinite'/%3E%3C/circle%3E%3Ccircle cx='62' cy='23' r='0.8' fill='%23a78bfa' opacity='0.4'%3E%3Canimate attributeName='opacity' values='0.4;0;0.4' dur='1.3s' repeatCount='indefinite'/%3E%3C/circle%3E%3Ccircle cx='70' cy='27' r='0.6' fill='%238b5cf6' opacity='0.3'%3E%3Canimate attributeName='opacity' values='0.3;0;0.3' dur='0.8s' repeatCount='indefinite'/%3E%3C/circle%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-size: contain;

  @media (max-width: 768px) {
    opacity: 0.15;
    width: 50px;
    height: 25px;
  }
`;

/* ==================== FLOATING CANDLES — Great Hall ==================== */
const candleFloat = keyframes`
  0%, 100% { transform: translateY(0); }
  30% { transform: translateY(-8px); }
  60% { transform: translateY(4px); }
`;

const flameFlicker = keyframes`
  0%, 100% { transform: scaleX(1) scaleY(1); opacity: 0.9; }
  25% { transform: scaleX(0.85) scaleY(1.1); opacity: 1; }
  50% { transform: scaleX(1.1) scaleY(0.9); opacity: 0.85; }
  75% { transform: scaleX(0.9) scaleY(1.05); opacity: 0.95; }
`;

const glowPulse = keyframes`
  0%, 100% { opacity: 0.15; transform: scale(1); }
  50% { opacity: 0.3; transform: scale(1.2); }
`;

const CandleField = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
`;

const FloatingCandle = styled.div<{ $i: number; $x: number; $y: number }>`
  position: absolute;
  left: ${({ $x }) => $x}%;
  top: ${({ $y }) => $y}%;
  display: flex;
  flex-direction: column;
  align-items: center;
  animation: ${candleFloat} ${({ $i }) => 4 + ($i % 5) * 0.8}s ease-in-out infinite;
  animation-delay: ${({ $i }) => $i * 0.6}s;
  opacity: ${({ $i }) => 0.3 + ($i % 3) * 0.15};

  @media (max-width: 768px) {
    opacity: ${({ $i }) => 0.15 + ($i % 3) * 0.08};
  }
`;

const CandleBody = styled.div<{ $h: number }>`
  width: 4px;
  height: ${({ $h }) => $h}px;
  background: linear-gradient(180deg, #e8dcc0 0%, #d4c8a0 40%, #c0b080 100%);
  border-radius: 1px 1px 2px 2px;
  position: relative;

  /* Wax drip */
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -1px;
    width: 6px;
    height: 3px;
    background: #e0d4b8;
    border-radius: 0 0 3px 3px;
  }
`;

const CandleFlame = styled.div<{ $i: number }>`
  width: 6px;
  height: 12px;
  border-radius: 50% 50% 50% 50% / 60% 60% 40% 40%;
  background: radial-gradient(ellipse at 50% 60%,
    rgba(255, 255, 220, 0.95) 0%,
    rgba(255, 200, 50, 0.8) 30%,
    rgba(255, 120, 20, 0.5) 60%,
    transparent 100%
  );
  margin-bottom: 1px;
  transform-origin: bottom center;
  animation: ${flameFlicker} ${({ $i }) => 0.8 + ($i % 4) * 0.15}s ease-in-out infinite;
  animation-delay: ${({ $i }) => $i * 0.2}s;
  order: -1;
`;

const CandleGlow = styled.div<{ $i: number }>`
  position: absolute;
  top: -15px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 200, 80, 0.2), transparent 70%);
  animation: ${glowPulse} ${({ $i }) => 2 + ($i % 3) * 0.5}s ease-in-out infinite;
  animation-delay: ${({ $i }) => $i * 0.3}s;
  filter: blur(4px);
`;

/* ==================== WANDERING WAND ==================== */
const wandSparkPop = keyframes`
  0%, 100% { opacity: 0; transform: scale(0); }
  30% { opacity: 1; transform: scale(1.2); }
  70% { opacity: 0.5; transform: scale(0.7); }
`;

const WandFloat = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  pointer-events: none;
  will-change: transform;
  filter: drop-shadow(0 0 8px rgba(255, 200, 50, 0.2));
`;

const WandSparkle = styled.div<{ $x: number; $y: number; $d: number }>`
  position: absolute;
  left: ${({ $x }) => $x}px;
  top: ${({ $y }) => $y}px;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #ffd700;
  box-shadow: 0 0 4px #ffd700, 0 0 8px rgba(255, 200, 50, 0.3);
  animation: ${wandSparkPop} ${({ $d }) => $d}s ease-in-out infinite;
  animation-delay: ${({ $d }) => $d * 0.3}s;
`;
